/**
 * way_ token validation via backend API.
 *
 * Used by MCP servers to get userId/email for way_ tokens.
 * Calls GET /api/auth/me on the backend (which validates via KV lookup).
 */

export interface WayTokenValidation {
  valid: true;
  userId: string;
  email: string;
  /** Session id from /api/auth/me. For a harness token this is the conversation_id. */
  sessionId?: string;
  /** True when the token is a synthetic harness token (harness-agents PR 2.7c). */
  isHarness?: boolean;
}

export interface WayTokenError {
  valid: false;
  error: string;
}

/**
 * Validate a way_ token by calling the backend's /api/auth/me endpoint.
 */
export async function validateWayToken(
  token: string,
  backendUrl: string,
): Promise<WayTokenValidation | WayTokenError> {
  try {
    const response = await fetch(`${backendUrl}/api/auth/me`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-Source': 'mcp',
      },
    });

    if (!response.ok) {
      return { valid: false, error: 'Invalid or expired token' };
    }

    const data = await response.json() as {
      user_id: string; email: string; org_id: string; session_id?: string; is_harness?: boolean;
    };
    return {
      valid: true,
      userId: data.user_id,
      email: data.email,
      ...(data.session_id ? { sessionId: data.session_id } : {}),
      ...(data.is_harness ? { isHarness: true } : {}),
    };
  } catch {
    return { valid: false, error: 'Token validation failed' };
  }
}
